import { z } from 'zod';
import { AnalyticsPlatform, analyticsPlatformSchema } from '@common/interfaces/platform/AnalyticsPlatform';
import { BasketItemType } from '@common/interfaces/nexudus/api/rest/Basket';
import { ICommonEventFields, ICommonMCEvent } from '@common/interfaces/eventEntries/CommonEvent';
import {
  EventEntryBasketItem,
  eventEntryBasketItemSchema,
} from '@common/interfaces/eventEntries/EventEntryBasketItem';

export const UPDATE_BASKET = 'update_basket' as const;

/**
 * Client DTO for the UpdateBasketEvent to be emitted by the passport app or portal frontend.
 *
 * Does not contain identity information as this will come from the JWT before enqueueing.
 */
export interface IUpdateBasketClientDTO {
  /**
   * The platform from which the event was emitted.
   */
  platform: AnalyticsPlatform;
  /**
   * ISO formatted datetime for the event
   */
  timestamp: string;
  /**
   * The item that was updated in the basket
   */
  updatedItem: EventEntryBasketItem;

  /**
   * The entire contents of the basket so far
   */
  basketItems: EventEntryBasketItem[];

  /**
   * Value of the basket in GBP
   */
  basketValue: number;
}

/**
 * Based on IUpdateBasketClientDTO, but contains the identity of the user as verified using their credentials in the
 * portal backend
 */
export interface IUpdateBasketQueueDTO extends IUpdateBasketClientDTO {
  identity: {
    email: string;
  };
}

export const updateBasketClientDTOSchema = z.object({
  platform: analyticsPlatformSchema,
  timestamp: z.string(),
  updatedItem: eventEntryBasketItemSchema,
  basketItems: z.array(eventEntryBasketItemSchema),
  basketValue: z.number(),
}) satisfies z.ZodType<IUpdateBasketClientDTO>;

export const updateBasketQueueDTOSchema = z.object({
  platform: analyticsPlatformSchema,
  timestamp: z.string(),
  basketItems: z.array(eventEntryBasketItemSchema),
  updatedItem: eventEntryBasketItemSchema,
  basketValue: z.number(),
  identity: z.object({
    email: z.string(),
  }),
}) satisfies z.ZodType<IUpdateBasketQueueDTO>;

/**
 * The domain object for the entry event for "update basket".
 *
 * This is fully specified and should be created in the worker by performing the needed lookups just before
 * reporting to SF:MC
 */
export interface IUpdateBasketDO extends ICommonEventFields {
  /**
   * The item that was updated in the basket
   */
  updatedItem: EventEntryBasketItem;

  /**
   * List of items in the user's basket
   */
  basketItems: EventEntryBasketItem[];

  /**
   * Value of the basket in GBP
   */
  basketValue: number;
}

/**
 * The formatted event to be sent to SF:MC in the schema they desire
 */
export interface IUpdateBasketMCEvent extends ICommonMCEvent {
  /**
   * The type of the item that was updated in the basket, e.g. "booking", "event", "contract", "product", "course"
   */
  UpdatedItemType: BasketItemType;
  /**
   * The item that was updated in the basket
   */
  UpdatedItemId: string;
  /**
   * The item that was updated in the basket
   */
  UpdatedItemName: string;
  /**
   * The item that was updated in the basket
   */
  UpdatedItemPrice: number | null;
  /**
   * The quantity of the item that was updated in the basket.
   */
  UpdatedItemQuantity: number | null;

  /**
   * JSON formatted string containing the items in the user's basket
   */
  Bookings: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Products: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Events: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Contracts: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Courses: string | null;

  /**
   * The total value of the invoice
   */
  BasketValue: number;
}
